import { htmlSafe } from '@ember/template';
import type { SafeString } from '@ember/template/-private/handlebars';
import { DivIcon } from 'leaflet';
import compact from 'lodash/compact';

export interface IconOptions {
  url?: string;
  size?: number;
  classNames?: string[];
  testAttributeLabel?: string;
}

export interface MapIconOptions {
  [state: string]: IconOptions;
}

export interface LabelOptions {
  classNames?: string[];
  testAttributeLabel?: string;
  value?: string;
}

export interface MapIconLabelOptions {
  [state: string]: LabelOptions;
}

function monogramInitials(fullName: string | null | undefined): string {
  // Handle null or undefined by using empty string
  const nameAttr = fullName ?? '';

  // Remove "+123" style suffixes if present
  const cleanedName = /\+\d+/.test(nameAttr) ? (nameAttr.split('+')[0] as string) : nameAttr;

  // Split name into parts and collect first letter of each part
  const nameArray = compact(cleanedName.split(' '));
  const initials = nameArray.map((namePart) => namePart.charAt(0)).join('');

  // Limit to maximum of 4 characters
  return initials.slice(0, 4);
}

function monogramLengthClass(name: string): string {
  const initials = monogramInitials(name);

  switch (initials.length) {
    case 2:
      return 'two';
    case 3:
      return 'three';
    case 4:
      return 'four';
  }
  return '';
}

export function buildProfilePictureIcon(src: string): DivIcon {
  const html = htmlSafe(`
    <div aria-hidden="true" class="table-cell align-middle w-[30px] min-w-[30px]" data-test-profile-picture>
      <span
        class="block mx-auto overflow-hidden border-4 border-white box-content rounded-full w-6 h-6"
        style="background-color: hsl(224, 8%, 48%); border: 4px solid white;"
      >
        <img
          alt=""
          class="object-cover object-center w-6 h-6"
          src="${src}"
          role="presentation"
          data-test-entry-feed-photo
          data-test-entry-thumbnail
        >
      </span>
    </div>
  `);

  return new DivIcon({
    iconSize: [30, 30],
    className: '',
    html: html.toString(),
  });
}

export function buildMonogramIcon(name: string): DivIcon {
  const html = htmlSafe(`
    <div aria-hidden="true" class="table-cell align-middle w-[30px] min-w-[30px]" data-test-monogram>
      <div
        data-test-entry-monogram
        class="monogram small !w-[30px] !h-[30px] ${monogramLengthClass(name)}"
        style="background-color: hsl(224, 8%, 48%)"
      >
        ${monogramInitials(name)}
      </div>
    </div>
  `);

  return new DivIcon({
    iconSize: [30, 30],
    className: '',
    html: html.toString(),
  });
}

export function buildResourceIcon(iconOptions?: IconOptions, labelOptions?: LabelOptions): DivIcon | undefined {
  if (!iconOptions) return;
  const combinedClassNames = iconOptions.classNames?.join(' ') || '';

  const iconHtml = generateIconHtml(iconOptions, labelOptions);
  if (!iconHtml) return;

  return new DivIcon({
    iconSize: [30, 30],
    className: `resource-icon-base ${combinedClassNames}`,
    html: generateIconHtml(iconOptions, labelOptions)?.toString(),
  });
}

function generateIconHtml(iconOptions: IconOptions, labelOptions?: LabelOptions): SafeString | undefined {
  if (!iconOptions) return;

  const { size, testAttributeLabel } = iconOptions;
  const sizeValue = size || 30;
  const testAttr = testAttributeLabel || '';
  const labelSpan = labelOptions
    ? `<span class="${labelOptions.classNames?.join(' ') || ''}" data-test-resource-icon-label>${labelOptions.value}</span>`
    : '';
  const containerClass = `flex justify-start items-center bg-contain bg-no-repeat ${labelOptions ? 'w-40' : ''}`.trim();

  return htmlSafe(`
    <div
        aria-hidden="true"
        class="${containerClass}"
        ${testAttr}
      >
        <div class="w-[${sizeValue}px] h-[${sizeValue}px]"></div>
        ${labelSpan}
      </div>`);
}
